import type { Dsr } from 'src/app/state-man/models/dsr.model'
import type { ExpenseReport } from 'src/app/state-man/models/expense-report.model'
import type { Form } from 'src/app/state-man/models/form.model'
import type { Prf } from 'src/app/state-man/models/prf.model'
import type { Ticket } from 'src/app/state-man/models/ticket.model'
import type { Wor } from 'src/app/state-man/models/wor.model'
import { LoggerService } from 'src/app/services/logger.service'

/** Mirrors the $rt-tones map in theme/rt/_primitives.scss. Keep the two in step. */
export type Tone =
  | 'progress'   // #E07000 In Progress, draft, open expense report
  | 'submitted'  // #7A3FC4 Submitted, waiting
  | 'approved'   // #1E874B Approved, paid, WO created ("Open")
  | 'rejected'   // #D6081A Rejected, due
  | 'dnr'        // #0C0D0F Rejected · Do Not Resubmit
  | 'muted'      // #6B747E idle, read-only notes, "New"
  | 'info'       // #1F6FD1 Scope of Work
  | 'amber'      // #E0A100 Callouts amber bar

export interface StatusDefBase<K extends string> {
  readonly key: K
  readonly label: string
  readonly tone: Tone
  /** Editor opens read-only; the list button says "View" instead of "Edit". */
  readonly readOnly: boolean
  readonly deletable: boolean
  /** Already left the device, so deleting asks first. */
  readonly sent: boolean
}

export type PaperStatus = 'inProgress' | 'submitted' | 'approved' | 'rejected' | 'dnr'

export interface PaperStatusDef extends StatusDefBase<PaperStatus> {
  /** Makes a "Rejected X" Callouts card until resubmitted or deleted. */
  readonly callout: boolean
  /** Job-card count pill noun: [one, many]. */
  readonly pill: readonly [one: string, many: string]
  /** DSR time counts on the Timesheet (the server counts Submitted and Approved). */
  readonly feedsTimesheet: boolean
}

export const PAPER_STATUS = {
  inProgress: { key: 'inProgress', label: 'Draft', tone: 'progress', readOnly: false, deletable: true, sent: false, callout: false, pill: ['draft', 'drafts'], feedsTimesheet: false },
  submitted: { key: 'submitted', label: 'Submitted', tone: 'submitted', readOnly: true, deletable: true, sent: true, callout: false, pill: ['submitted', 'submitted'], feedsTimesheet: false },
  approved: { key: 'approved', label: 'Approved', tone: 'approved', readOnly: true, deletable: false, sent: true, callout: false, pill: ['approved', 'approved'], feedsTimesheet: true },
  rejected: { key: 'rejected', label: 'Rejected', tone: 'rejected', readOnly: false, deletable: true, sent: true, callout: true, pill: ['rejected', 'rejected'], feedsTimesheet: false },
  dnr: { key: 'dnr', label: 'Rejected · Do Not Resubmit', tone: 'dnr', readOnly: true, deletable: true, sent: true, callout: false, pill: ['do not resubmit', 'do not resubmit'], feedsTimesheet: false },
} as const satisfies { readonly [K in PaperStatus]: PaperStatusDef & { readonly key: K } }

/** One order for filter chips (after "All"), job-card count pills and group status bars. */
export const PAPER_STATUS_ORDER = ['approved', 'submitted', 'inProgress', 'rejected', 'dnr'] as const satisfies readonly PaperStatus[]

export type WorStatus = 'inProgress' | 'submitted' | 'woCreated' | 'rejected'

export interface WorStatusDef extends StatusDefBase<WorStatus> {
  /** Dashed placeholder card on est_start_date: 'waiting' on admin, or 'created' while the WO is not yet in the tech's daily work. */
  readonly placeholder: 'waiting' | 'created' | false
}

export const WOR_STATUS = {
  inProgress: { key: 'inProgress', label: 'Draft', tone: 'progress', readOnly: false, deletable: true, sent: false, placeholder: false },
  submitted: { key: 'submitted', label: 'Submitted', tone: 'submitted', readOnly: true, deletable: true, sent: true, placeholder: 'waiting' },
  woCreated: { key: 'woCreated', label: 'Open', tone: 'approved', readOnly: true, deletable: false, sent: true, placeholder: 'created' },
  rejected: { key: 'rejected', label: 'Rejected', tone: 'rejected', readOnly: false, deletable: true, sent: true, placeholder: false },
} as const satisfies { readonly [K in WorStatus]: WorStatusDef & { readonly key: K } }

export const WOR_STATUS_ORDER = ['inProgress', 'submitted', 'woCreated', 'rejected'] as const satisfies readonly WorStatus[]

export type ExpenseStatus = 'open' | 'submitted' | 'approved' | 'paid' | 'rejected'

export const EXPENSE_STATUS = {
  open: { key: 'open', label: 'Open', tone: 'progress', readOnly: false, deletable: true, sent: false },
  submitted: { key: 'submitted', label: 'Submitted', tone: 'submitted', readOnly: true, deletable: false, sent: true },
  approved: { key: 'approved', label: 'Approved', tone: 'approved', readOnly: true, deletable: false, sent: true },
  paid: { key: 'paid', label: 'Paid', tone: 'approved', readOnly: true, deletable: false, sent: true },
  rejected: { key: 'rejected', label: 'Rejected', tone: 'rejected', readOnly: false, deletable: true, sent: true },
} as const satisfies { readonly [K in ExpenseStatus]: StatusDefBase<ExpenseStatus> & { readonly key: K } }

export type TicketStatus = 'open' | 'inProgress' | 'resolved' | 'onHold'

export const TICKET_STATUS = {
  open: { key: 'open', label: 'Open', tone: 'submitted', readOnly: true, deletable: false, sent: true },
  inProgress: { key: 'inProgress', label: 'In Progress', tone: 'progress', readOnly: true, deletable: false, sent: true },
  resolved: { key: 'resolved', label: 'Resolved', tone: 'approved', readOnly: true, deletable: false, sent: true },
  onHold: { key: 'onHold', label: 'On Hold', tone: 'muted', readOnly: true, deletable: false, sent: true },
} as const satisfies { readonly [K in TicketStatus]: StatusDefBase<TicketStatus> & { readonly key: K } }

/** Filter chips after "All". An On Hold ticket shows under All only. */
export const TICKET_STATUS_ORDER = ['open', 'inProgress', 'resolved'] as const satisfies readonly TicketStatus[]

export const HIDDEN = 'hidden' as const
export type Hidden = typeof HIDDEN

/** id → key per item kind. The only place a status id is interpreted. */
export const STATUS_IDS = {
  dsr: { 0: 'inProgress', 1: 'submitted', 2: 'rejected', 3: 'approved', 4: 'dnr', 5: 'inProgress' },                // 0 = legacy "Saved"
  form: { 0: 'submitted', 1: 'rejected', 2: 'approved', 3: 'inProgress', 4: 'dnr' },
  prf: { 0: 'submitted', 1: 'rejected', 2: 'approved', 3: 'approved', 4: 'approved', 5: HIDDEN, 6: 'inProgress', 7: 'approved', 8: 'dnr' },   // 5 = legacy "Removed"
  wor: { 0: 'submitted', 1: 'woCreated', 2: 'rejected', 3: HIDDEN, 4: 'woCreated', 5: 'inProgress' },               // 3 Deleted, 4 Generated
  expense: { 0: 'open', 1: 'submitted', 2: 'submitted', 3: 'submitted', 4: 'paid', 5: 'rejected' },                  // 3 = waiting on AP
  ticket: { 1: 'open', 2: 'inProgress', 3: 'resolved', 4: 'onHold' } as Readonly<Record<number, TicketStatus | Hidden>>,  // rt2_it.ticket_statuses; 1 = Submitted
} as const satisfies {
  readonly dsr: Readonly<Record<number, PaperStatus | Hidden>>
  readonly form: Readonly<Record<number, PaperStatus | Hidden>>
  readonly prf: Readonly<Record<number, PaperStatus | Hidden>>
  readonly wor: Readonly<Record<number, WorStatus | Hidden>>
  readonly expense: Readonly<Record<number, ExpenseStatus | Hidden>>
  readonly ticket: Readonly<Record<number, TicketStatus | Hidden>>
}

export type StatusKind = keyof typeof STATUS_IDS
export type StatusKeyOf<K extends StatusKind> = Exclude<(typeof STATUS_IDS)[K][keyof (typeof STATUS_IDS)[K]], Hidden>

const UNKNOWN_FALLBACK: { readonly [K in StatusKind]: StatusKeyOf<K> } =
  { dsr: 'submitted', form: 'submitted', prf: 'submitted', wor: 'submitted', expense: 'submitted', ticket: 'open' }

const reported = new Set<string>()

/**
 * Interpret a status id. An unknown id is logged once and shown as the family's
 * read-only fallback, never hidden, so a new server status can't make paperwork vanish.
 */
export function statusOf<K extends StatusKind>(kind: K, id: number | null | undefined): StatusKeyOf<K> | Hidden {
  const table = STATUS_IDS[kind] as Readonly<Record<number, StatusKeyOf<K> | Hidden>>
  const key = id == null ? undefined : table[id]
  if (key !== undefined) return key
  const tag = `${kind}:${id}`
  if (!reported.has(tag)) {
    reported.add(tag)
    LoggerService.log(`Unknown ${kind} status id ${id}`)
  }
  return UNKNOWN_FALLBACK[kind]
}

export const dsrStatus = (d: Dsr) => statusOf('dsr', d.dsr_status_id)
export const formStatus = (f: Form) => statusOf('form', f.form_status_id)
export const prfStatus = (m: Prf) => statusOf('prf', m.prf_status_id)
export const worStatus = (w: Wor) => statusOf('wor', w.work_order_request_status_id)
export const expenseStatus = (e: ExpenseReport) => statusOf('expense', e.expense_status_id)
/** A ticket saved on the device has no status id until it uploads, and reads as open. */
export function ticketStatus(t: Pick<Ticket, 'ticket_status_id'>): TicketStatus {
  const key = t.ticket_status_id == null ? 'open' : statusOf('ticket', t.ticket_status_id)
  return key === HIDDEN ? 'open' : key
}

/** rt2.work_order_statuses: 0 is Open; every other id (Pending Approval, Invoiced, Inactive, …) reads as Closed. */
export const isWorkOrderOpen = (statusId: number | null | undefined): boolean => statusId === 0

/** Who a time entry waits on. */
export type TimeApprovalStage = 'tech' | 'manager' | 'done'

/** rt2.time_approval_statuses: 0 Pending Employee Approval, 1 Skipped (also what the device writes until the
 *  approval uploads), 2 Pending Manager Approval, 3 Pending EM, 4 Pending HR, 5 Approved. */
export const TIME_APPROVAL_STAGE: Readonly<Record<number, TimeApprovalStage>> =
  { 0: 'tech', 1: 'manager', 2: 'manager', 3: 'done', 4: 'done', 5: 'done' }

/** "Delete TOF-PRV-87987-2? It was submitted and will be removed for you and your manager. This can’t be undone." | null (no confirm). */
export function deletePrompt(def: StatusDefBase<string>, number: string): string | null {
  return def.sent ? `Delete ${number}? It was ${def.label.toLowerCase()} and will be removed for you and your manager. This can’t be undone.` : null
}

/** key → wire id for the two keys a tech ever writes. Editors never carry literal ids. */
export const STATUS_ID_FOR = {
  dsr: { inProgress: 5, submitted: 1 },
  form: { inProgress: 3, submitted: 0 },
  prf: { inProgress: 6, submitted: 0 },
  wor: { inProgress: 5, submitted: 0 },
  expense: { open: 0, submitted: 1 },        // 1 = Pending AM
} as const

/** The legacy `status` string the API payloads still carry. Written, never read by the UI. */
export const LEGACY_STATUS_NAME: { readonly [K in PaperStatus]: string } =
  { inProgress: 'InProgress', submitted: 'Submitted', approved: 'Approved', rejected: 'Rejected', dnr: 'Rejected Do Not Resubmit' }
